import { AppRoutes } from 'config/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useTranslation } from 'react-i18next';
import {
  useCreateOperationalRequestProductionDocumentMutation,
  useCreateSupplyProductionDocumentMutation,
  useGetOperationalRequestDocumentSchemeQuery,
  useGetCoreDocumentsNoCacheMutation,
  useSignDocumentMutation,
} from 'store/api/documents';
import { useHandleMutation } from 'utils/handleMutation';
import {
  generateModelFromScheme,
  generateValidationSchema,
  parseSteps,
} from 'utils/sheme-parser';

export const useNewRequest = () => {
  const { id: documentId } = useParams();
  const navigate = useNavigate();

  const {
    i18n: { language },
  } = useTranslation();

  const path = useLocation().pathname;
  const { Home } = AppRoutes.Dashboard;

  const [step, setStep] = useState<number>(1);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string | undefined>();

  const [fetchDocument] = useGetCoreDocumentsNoCacheMutation({});

  const [signDocument, signDocumentMutation] = useSignDocumentMutation();

  const { data: operatonalRequestDocumentScheme } = useGetOperationalRequestDocumentSchemeQuery(
    {
      documentId: documentId ?? '',
    },
    { skip: false },
  );

  useCreateSupplyProductionDocumentMutation();
  const [createOperationalRequestDocument, createOperationalRequestDocumentMutation] =
    useCreateOperationalRequestProductionDocumentMutation();

  const dropDownOptions = useMemo(
    () =>
      [
        operatonalRequestDocumentScheme && {
          value: 'OperatonalRequest',
          label: 'Оперативная заявка',
          scheme: operatonalRequestDocumentScheme,
          templateId: '769130c3-1561-48fd-a939-c93448c0e029',
        },
      ]?.filter((item) => item),
    [operatonalRequestDocumentScheme],
  );

  const [currentSelect, setCurrentSelect] = useState(dropDownOptions[0]);
  const isApproval = path.includes(AppRoutes.Dashboard.Contracts.ContractApproval);

  const handleSign = useCallback(
    async (id?: string, version?: number) => {
      const { data } = (await fetchDocument({ ids: [documentId ?? contractId ?? ''] })) as any;

      const [document] = data ?? [];

      await signDocument({
        signature: null,
        detachedSignature: null,
        documentId: id ?? document?.id ?? '',
        documentVersion: version ?? document?.version ?? 0,
      });
    },
    [fetchDocument, documentId, contractId, signDocument],
  );

  useEffect(() => {
    if (contractId?.length) {
      handleSign();
    }
  }, [contractId]);

  const onSuccessCreate = useCallback(async (id: string) => {
    setContractId(id);
  }, []);

  const handleClose = useCallback(() => {
    navigate(Home + AppRoutes.Dashboard.OperationalRequests.Home);
  }, [navigate]);

  const onSuccessSign = useCallback(async () => {
    setIsProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 5000));
    setIsProcessing(false);
    handleClose();
  }, [handleClose]);

  const onErrorSign = useCallback((err: any) => {
    console.error('Error sign', err);
  }, []);

  useHandleMutation({
    ...createOperationalRequestDocumentMutation,
    onSuccess: onSuccessCreate,
  });

  useHandleMutation({
    ...signDocumentMutation,
    onSuccess: onSuccessSign,
    onError: onErrorSign,
  });

  useEffect(() => {
    setCurrentSelect(dropDownOptions[0]);
  }, [dropDownOptions, operatonalRequestDocumentScheme]);

  const { currentScheme, model, steps, stepsLabels } = useMemo(() => {
    return {
      currentScheme: currentSelect?.scheme,
      model: generateModelFromScheme(currentSelect?.scheme, language),
      ...parseSteps(currentSelect?.scheme),
    };
  }, [currentSelect]);

  const getValidationSchema = () => {
    if (isApproval) {
      return Yup.object().shape({});
    }
    return generateValidationSchema(currentScheme, step);
  };

  return {
    step,
    steps,
    model,
    setStep,
    handleSign,
    isApproval,
    stepsLabels,
    handleClose,
    currentSelect,
    currentScheme,
    dropDownOptions,
    setCurrentSelect,
    createOperationalRequestDocument,
    getValidationSchema,
    isProcessing,
    isLoading:
      signDocumentMutation.isLoading || createOperationalRequestDocumentMutation.isLoading,
  };
};
